<script lang="ts">
import { computed, defineComponent, reactive, toRefs, watch } from 'vue';
import type { PropType } from 'vue';
import GroupableItem from '@/src/components/GroupableItem.vue';
import { DataSelection, isDicomImage } from '@/src/utils/dataSelection';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import {
  getVolumeDisplayName,
  getVolumeTooltip,
} from '@/src/utils/dicomDisplay';
import { useDatasetStore } from '@/src/store/datasets';
import { useMultiSelection } from '@/src/composables/useMultiSelection';
import { useMessageStore } from '@/src/store/messages';
import { useLayersStore } from '@/src/store/datasets-layers';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { IMAGE_DRAG_MEDIA_TYPE } from '@/src/constants';
import { useViewStore } from '@/src/store/views';

function dicomCacheKey(volKey: string) {
  return `dicom-${volKey}`;
}

type Thumbnail =
  | { kind: 'image'; value: string }
  | { kind: 'text'; value: string };

export default defineComponent({
  name: 'PatientStudyVolumeBrowser',
  props: {
    volumeKeys: {
      type: Array as PropType<Array<string>>,
      required: true,
    },
  },
  components: {
    GroupableItem,
  },
  setup(props) {
    const { volumeKeys } = toRefs(props);
    const dicomStore = useDICOMStore();
    const datasetStore = useDatasetStore();
    const layersStore = useLayersStore();
    const imageCacheStore = useImageCacheStore();
    const viewStore = useViewStore();

    const { currentImageID } = useCurrentImage();
    const volumes = computed(() => {
      const volumeInfo = dicomStore.volumeInfo;
      const primarySelection = currentImageID.value;
      const layerVolumes = layersStore
        .getLayers(primarySelection)
        .filter(({ selection }) => isDicomImage(selection));
      const layerVolumeKeys = layerVolumes.map(({ selection }) => selection);
      const loadedLayerVolumeKeys = layerVolumes
        .filter(({ id }) => imageCacheStore.imageById[id]?.isLoaded())
        .map(({ selection }) => selection);
      const selectedVolumeKey =
        isDicomImage(primarySelection) && primarySelection;

      return volumeKeys.value.map((volumeKey) => {
        const selectionKey = volumeKey as DataSelection;
        const isLayer = layerVolumeKeys.includes(volumeKey);
        const layerLoaded = loadedLayerVolumeKeys.includes(volumeKey);
        const layerLoading = isLayer && !layerLoaded;
        const layerable = volumeKey !== selectedVolumeKey && primarySelection;
        return {
          key: volumeKey,
          // for thumbnailing
          cacheKey: dicomCacheKey(volumeKey),
          info: volumeInfo[volumeKey],
          name: getVolumeDisplayName(volumeInfo[volumeKey]),
          // for UI selection
          selectionKey,
          isLayer,
          layerable,
          layerLoading,
          layerHandler: () => {
            if (!layerLoading && layerable) {
              if (isLayer)
                layersStore.deleteLayer(primarySelection, selectionKey);
              else layersStore.addLayer(primarySelection, selectionKey);
            }
          },
        };
      });
    });

    // --- thumbnails --- //

    const thumbnailCache = reactive<Record<string, Thumbnail>>({});

    watch(
      volumeKeys,
      (keys) => {
        keys.forEach(async (key) => {
          const cacheKey = dicomCacheKey(key);
          if (cacheKey in thumbnailCache) {
            return;
          }

          const image = imageCacheStore.imageById[key];
          if (!image) return;

          try {
            const thumb = await image.getThumbnail();
            if (thumb) {
              thumbnailCache[cacheKey] = { kind: 'image', value: thumb };
            } else {
              thumbnailCache[cacheKey] = {
                kind: 'text',
                value: dicomStore.volumeInfo[key].Modality,
              };
            }
          } catch (err) {
            if (err instanceof Error) {
              const messageStore = useMessageStore();
              messageStore.addError('Failed to generate thumbnails', {
                error: err,
                details: `${err}. More details can be found in the developer's console.`,
              });
            }
            thumbnailCache[cacheKey] = {
              kind: 'text',
              value: dicomStore.volumeInfo[key].Modality,
            };
          }
        });

        // deletion case
        const lookup = new Set(keys.map((key) => dicomCacheKey(key)));
        Object.keys(thumbnailCache).forEach((key) => {
          if (!lookup.has(key)) {
            delete thumbnailCache[key];
          }
        });
      },
      { immediate: true, deep: true }
    );

    // --- selection --- //

    const { selected, selectedAll, selectedSome, toggleSelectAll } =
      useMultiSelection(volumeKeys);

    const removeData = (key: string) => {
      datasetStore.remove(key);
    };

    const removeSelectedDICOMVolumes = () => {
      // make copy of selected as removing selected will change the array
      [...selected.value].forEach(removeData);

      selected.value = [];
    };

    // dragging

    function onDragStart(imageID: string, event: DragEvent) {
      event.dataTransfer?.setData(IMAGE_DRAG_MEDIA_TYPE, imageID);
    }

    function showInAllViews(volumeKey: string) {
      viewStore.setDataForAllViews(volumeKey);
    }

    return {
      selected,
      selectedAll,
      selectedSome,
      toggleSelectAll,
      thumbnailCache,
      volumes,
      removeData,
      removeSelectedDICOMVolumes,
      onDragStart,
      showInAllViews,
      getVolumeTooltip,
    };
  },
});
</script>

<template>
  <v-container class="pa-0">
    <v-row no-gutters justify="space-between">
      <v-col cols="6" align-self="center">
        <v-checkbox
          class="ml-3 align-center justify-center"
          :indeterminate="selectedSome && !selectedAll"
          label="Select All"
          v-model="selectedAll"
          @click.stop="toggleSelectAll"
          density="compact"
          hide-details
        />
      </v-col>
      <v-col cols="6" align-self="center" class="d-flex justify-end mt-2">
        <v-btn
          icon
          variant="text"
          :disabled="!selectedSome"
          @click.stop="removeSelectedDICOMVolumes"
        >
          <v-icon>mdi-delete</v-icon>
          <v-tooltip location="left" activator="parent">
            Delete selected
          </v-tooltip>
        </v-btn>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col>
        <div class="my-1 volume-list">
          <groupable-item
            v-for="volume in volumes"
            :key="volume.info.VolumeID"
            v-slot:default="{ active, select }"
            :value="volume.selectionKey"
          >
            <div
              :class="{
                'volume-card': true,
                'volume-card-active': active,
              }"
              :title="getVolumeTooltip(volume.info)"
              draggable="true"
              @click="select"
              @dragstart="onDragStart(volume.info.VolumeID, $event)"
            >
              <div class="thumbnail-container">
                <v-img
                  cover
                  height="52"
                  width="52"
                  :src="
                    (thumbnailCache[volume.cacheKey] &&
                      thumbnailCache[volume.cacheKey].kind === 'image' &&
                      thumbnailCache[volume.cacheKey].value) ||
                    ''
                  "
                >
                  <template v-slot:placeholder>
                    <div class="thumbnail-placeholder">
                      <v-progress-circular
                        v-if="thumbnailCache[volume.cacheKey] === undefined"
                        indeterminate
                        size="18"
                        width="2"
                        color="grey-lighten-1"
                      />
                      <span
                        v-else-if="
                          thumbnailCache[volume.cacheKey] &&
                          thumbnailCache[volume.cacheKey].kind === 'text'
                        "
                        class="thumbnail-modality"
                      >
                        {{ thumbnailCache[volume.cacheKey].value }}
                      </span>
                    </div>
                  </template>
                </v-img>
              </div>

              <div class="volume-meta">
                <div class="volume-meta__name text-ellipsis">
                  {{ volume.name }}
                </div>
                <div class="volume-meta__sub">
                  {{ volume.info.Modality }} ·
                  {{ volume.info.NumberOfSlices }} slices
                  <span v-if="volume.isLayer" class="volume-meta__badge">
                    layer
                  </span>
                </div>
              </div>

              <div class="volume-actions" @click.stop>
                <v-checkbox
                  :key="volume.info.VolumeID"
                  :value="volume.key"
                  v-model="selected"
                  density="compact"
                  hide-details
                  class="series-selector"
                />
                <v-btn
                  icon
                  variant="plain"
                  size="x-small"
                  class="dataset-menu"
                  data-testid="dataset-menu-button"
                >
                  <v-menu activator="parent">
                    <v-list density="compact">
                      <v-list-item
                        v-if="volume.layerable"
                        @click.stop="volume.layerHandler()"
                        data-testid="dataset-menu-layer-item"
                      >
                        <template v-if="volume.layerLoading">
                          <div style="margin: 0 auto">
                            <v-progress-circular indeterminate size="small" />
                          </div>
                        </template>
                        <template v-else>
                          <span v-if="volume.isLayer">Remove as layer</span>
                          <span v-else>Add as layer</span>
                        </template>
                      </v-list-item>
                      <v-list-item @click="showInAllViews(volume.key)">
                        Show in all views
                      </v-list-item>
                      <v-list-item @click="removeData(volume.key)">
                        Delete
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <v-icon size="medium">mdi-dots-vertical</v-icon>
                </v-btn>
              </div>
            </div>
          </groupable-item>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.volume-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.volume-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px;
  cursor: pointer;
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius-sm, 6px);
  background: var(--pycad-elevated, #161616);
  transition:
    border-color 0.13s ease,
    background 0.13s ease;
  user-select: none;
}

.volume-card:hover {
  border-color: var(--pycad-border-strong, #3a3a3a);
  background: var(--pycad-hover, #1e1e1e);
}

.volume-card-active {
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.14));
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
}

.thumbnail-container {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 5px;
  overflow: hidden;
  background: #000;
  border: 1px solid var(--pycad-border, #282828);
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-modality {
  font-size: 11px;
  font-weight: 600;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.volume-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.volume-meta__name {
  font-size: 12px;
  font-weight: 500;
  color: var(--pycad-text-primary, #ffffff);
}

.volume-meta__sub {
  font-size: 11px;
  color: var(--pycad-text-secondary, #a0a0a0);
  display: flex;
  align-items: center;
  gap: 6px;
}

.volume-meta__badge {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.4);
  color: #3b82f6;
}

.volume-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.series-selector {
  max-width: 36px;
}
</style>
